/**
 * Comprehensive Test Result Reporter with Outlook Integration
 * 
 * Categorizes test results by:
 * - User input
 * - Authentication method
 * - Graph API call type
 * - Pass/Fail status
 * 
 * Sends formatted reports via Outlook email with detailed insights
 */

import { createClient } from '@blinkdotnew/sdk';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

export interface TestResultDetails {
  id: string;
  name: string;
  category: string;
  userInput?: string;
  authMethod: 'blink_auth' | 'oauth_outlook' | 'oauth_gmail';
  apiCallType?: string;
  graphApiEndpoint?: string;
  httpMethod?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  startTime?: number;
  endTime?: number;
  duration?: number;
  result?: any;
  error?: string;
  errorDetails?: string;
  expectedResult?: string;
  actualResult?: string;
  apiCalls?: {
    endpoint: string;
    method: string;
    status: number;
    duration: number;
    requestBody?: any;
    responseBody?: any;
  }[];
}

export interface TestSummary {
  executionId: string;
  userId: string;
  userEmail: string;
  timestamp: string;
  totalTests: number;
  passed: number;
  failed: number;
  skipped: number;
  duration: number;
  testResults: TestResultDetails[];
  authBreakdown: {
    blink_auth: { passed: number; failed: number; total: number };
    oauth_outlook: { passed: number; failed: number; total: number };
    oauth_gmail: { passed: number; failed: number; total: number };
  };
  apiCallBreakdown: {
    [endpoint: string]: { passed: number; failed: number; total: number };
  };
  categoryBreakdown: {
    [category: string]: { passed: number; failed: number; total: number };
  };
}

export class TestResultReporter {
  private userId: string | null = null;
  private userEmail: string | null = null;

  async initialize(userId: string, userEmail: string) {
    this.userId = userId;
    this.userEmail = userEmail;
  }

  /**
   * Generate comprehensive test summary with categorization
   */
  generateSummary(testResults: TestResultDetails[]): TestSummary {
    if (!this.userId || !this.userEmail) {
      throw new Error('Reporter not initialized. Call initialize() first.');
    }

    const executionId = `test_${Date.now()}`;
    const timestamp = new Date().toISOString();

    // Calculate overall summary
    const passed = testResults.filter(t => t.status === 'success').length;
    const failed = testResults.filter(t => t.status === 'failed').length;
    const skipped = testResults.filter(t => t.status === 'skipped').length;
    const totalDuration = testResults.reduce((sum, t) => sum + (t.duration || 0), 0);

    // Auth method breakdown
    const authBreakdown = {
      blink_auth: { passed: 0, failed: 0, total: 0 },
      oauth_outlook: { passed: 0, failed: 0, total: 0 },
      oauth_gmail: { passed: 0, failed: 0, total: 0 }
    };

    testResults.forEach(test => {
      const auth = test.authMethod;
      authBreakdown[auth].total++;
      if (test.status === 'success') authBreakdown[auth].passed++;
      if (test.status === 'failed') authBreakdown[auth].failed++;
    });

    // API call type breakdown
    const apiCallBreakdown: Record<string, { passed: number; failed: number; total: number }> = {};
    
    testResults.forEach(test => {
      if (test.graphApiEndpoint) {
        const endpoint = test.graphApiEndpoint;
        if (!apiCallBreakdown[endpoint]) {
          apiCallBreakdown[endpoint] = { passed: 0, failed: 0, total: 0 };
        }
        apiCallBreakdown[endpoint].total++;
        if (test.status === 'success') apiCallBreakdown[endpoint].passed++;
        if (test.status === 'failed') apiCallBreakdown[endpoint].failed++;
      }
    });

    // Category breakdown
    const categoryBreakdown: Record<string, { passed: number; failed: number; total: number }> = {};
    
    testResults.forEach(test => {
      const category = test.category;
      if (!categoryBreakdown[category]) {
        categoryBreakdown[category] = { passed: 0, failed: 0, total: 0 };
      }
      categoryBreakdown[category].total++;
      if (test.status === 'success') categoryBreakdown[category].passed++;
      if (test.status === 'failed') categoryBreakdown[category].failed++;
    });

    return {
      executionId,
      userId: this.userId,
      userEmail: this.userEmail,
      timestamp,
      totalTests: testResults.length,
      passed,
      failed,
      skipped,
      duration: totalDuration,
      testResults,
      authBreakdown,
      apiCallBreakdown,
      categoryBreakdown
    };
  }

  /**
   * Generate HTML report for email
   */
  private generateHTMLReport(summary: TestSummary): string {
    const passRate = ((summary.passed / summary.totalTests) * 100).toFixed(1);
    const statusColor = summary.failed === 0 ? '#22c55e' : summary.failed > 5 ? '#ef4444' : '#f59e0b';

    let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; background: #f9fafb; padding: 20px; }
    .container { max-width: 800px; margin: 0 auto; background: white; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 12px 12px 0 0; }
    .header h1 { margin: 0; font-size: 28px; }
    .header p { margin: 10px 0 0; opacity: 0.9; }
    .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; padding: 30px; border-bottom: 1px solid #e5e7eb; }
    .summary-card { text-align: center; padding: 20px; background: #f9fafb; border-radius: 8px; }
    .summary-card .value { font-size: 32px; font-weight: bold; margin: 0; }
    .summary-card .label { font-size: 14px; color: #6b7280; margin: 5px 0 0; }
    .summary-card.total .value { color: #3b82f6; }
    .summary-card.passed .value { color: #22c55e; }
    .summary-card.failed .value { color: #ef4444; }
    .summary-card.duration .value { font-size: 24px; }
    .section { padding: 30px; }
    .section h2 { margin: 0 0 20px; font-size: 20px; color: #111827; }
    .breakdown { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-bottom: 30px; }
    .breakdown-card { padding: 15px; background: #f9fafb; border-radius: 8px; border-left: 4px solid #667eea; }
    .breakdown-card h3 { margin: 0 0 10px; font-size: 14px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
    .breakdown-card .stats { font-size: 18px; font-weight: bold; color: #111827; }
    .breakdown-card .stats .passed { color: #22c55e; }
    .breakdown-card .stats .failed { color: #ef4444; }
    .test-list { margin-top: 20px; }
    .test-item { padding: 15px; background: #f9fafb; border-radius: 8px; margin-bottom: 10px; border-left: 4px solid #d1d5db; }
    .test-item.success { border-left-color: #22c55e; }
    .test-item.failed { border-left-color: #ef4444; background: #fef2f2; }
    .test-item .test-header { display: flex; justify-content: between; align-items: center; margin-bottom: 8px; }
    .test-item .test-name { font-weight: 600; color: #111827; }
    .test-item .test-status { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; }
    .test-item .test-status.success { background: #dcfce7; color: #166534; }
    .test-item .test-status.failed { background: #fee2e2; color: #991b1b; }
    .test-item .test-meta { font-size: 13px; color: #6b7280; margin-bottom: 8px; }
    .test-item .test-error { background: #fff; padding: 12px; border-radius: 6px; border: 1px solid #fee2e2; color: #991b1b; font-size: 13px; font-family: monospace; margin-top: 8px; }
    .test-item .test-result { background: #fff; padding: 12px; border-radius: 6px; border: 1px solid #e5e7eb; font-size: 13px; margin-top: 8px; }
    .api-call { background: #fff; padding: 10px; border-radius: 6px; border: 1px solid #e5e7eb; margin-top: 8px; font-size: 12px; }
    .api-call .endpoint { font-weight: 600; color: #3b82f6; }
    .api-call .status-badge { display: inline-block; padding: 2px 8px; border-radius: 8px; font-weight: 600; margin-left: 8px; }
    .api-call .status-badge.success { background: #dcfce7; color: #166534; }
    .api-call .status-badge.error { background: #fee2e2; color: #991b1b; }
    .footer { padding: 30px; background: #f9fafb; border-radius: 0 0 12px 12px; text-align: center; color: #6b7280; font-size: 13px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🧪 Live Test Results Report</h1>
      <p>Execution ID: ${summary.executionId}</p>
      <p>Executed: ${new Date(summary.timestamp).toLocaleString()}</p>
      <p>User: ${summary.userEmail}</p>
    </div>

    <div class="summary">
      <div class="summary-card total">
        <div class="value">${summary.totalTests}</div>
        <div class="label">Total Tests</div>
      </div>
      <div class="summary-card passed">
        <div class="value">${summary.passed}</div>
        <div class="label">Passed (${passRate}%)</div>
      </div>
      <div class="summary-card failed">
        <div class="value">${summary.failed}</div>
        <div class="label">Failed</div>
      </div>
      <div class="summary-card duration">
        <div class="value">${(summary.duration / 1000).toFixed(2)}s</div>
        <div class="label">Duration</div>
      </div>
    </div>

    <div class="section">
      <h2>📊 Authentication Breakdown</h2>
      <div class="breakdown">
`;

    // Auth breakdown cards
    Object.entries(summary.authBreakdown).forEach(([auth, stats]) => {
      if (stats.total > 0) {
        html += `
        <div class="breakdown-card">
          <h3>${auth.replace('_', ' ').toUpperCase()}</h3>
          <div class="stats">
            <span class="passed">${stats.passed}</span> / 
            <span class="failed">${stats.failed}</span> / 
            ${stats.total}
          </div>
        </div>
`;
      }
    });

    html += `
      </div>

      <h2>🔌 Graph API Call Breakdown</h2>
      <div class="breakdown">
`;

    // API breakdown cards
    Object.entries(summary.apiCallBreakdown).slice(0, 6).forEach(([endpoint, stats]) => {
      const shortEndpoint = endpoint.split('/').pop() || endpoint;
      html += `
        <div class="breakdown-card">
          <h3>${shortEndpoint}</h3>
          <div class="stats">
            <span class="passed">${stats.passed}</span> / 
            <span class="failed">${stats.failed}</span> / 
            ${stats.total}
          </div>
        </div>
`;
    });

    html += `
      </div>

      <h2>📂 Category Breakdown</h2>
      <div class="breakdown">
`;

    // Category breakdown cards
    Object.entries(summary.categoryBreakdown).forEach(([category, stats]) => {
      html += `
        <div class="breakdown-card">
          <h3>${category}</h3>
          <div class="stats">
            <span class="passed">${stats.passed}</span> / 
            <span class="failed">${stats.failed}</span> / 
            ${stats.total}
          </div>
        </div>
`;
    });

    html += `
      </div>
    </div>

    <div class="section">
      <h2>📋 Detailed Test Results</h2>
      <div class="test-list">
`;

    // Show failed tests first
    const failedTests = summary.testResults.filter(t => t.status === 'failed');
    const passedTests = summary.testResults.filter(t => t.status === 'success');

    if (failedTests.length > 0) {
      html += '<h3 style="color: #ef4444; margin-bottom: 15px;">❌ Failed Tests</h3>';
      
      failedTests.forEach(test => {
        html += `
        <div class="test-item failed">
          <div class="test-header">
            <span class="test-name">${test.name}</span>
            <span class="test-status failed">FAILED</span>
          </div>
          <div class="test-meta">
            <strong>Category:</strong> ${test.category} | 
            <strong>Auth:</strong> ${test.authMethod} | 
            <strong>Duration:</strong> ${test.duration}ms
            ${test.graphApiEndpoint ? ` | <strong>API:</strong> ${test.graphApiEndpoint}` : ''}
            ${test.httpMethod ? ` (${test.httpMethod})` : ''}
          </div>
          ${test.userInput ? `<div class="test-meta"><strong>User Input:</strong> "${test.userInput}"</div>` : ''}
          ${test.error ? `<div class="test-error">❌ Error: ${test.error}</div>` : ''}
          ${test.errorDetails ? `<div class="test-error">Details: ${test.errorDetails}</div>` : ''}
          ${test.expectedResult ? `
            <div class="test-result">
              <strong>Expected:</strong> ${test.expectedResult}<br>
              <strong>Actual:</strong> ${test.actualResult || 'N/A'}
            </div>
          ` : ''}
          ${test.apiCalls && test.apiCalls.length > 0 ? test.apiCalls.map(call => `
            <div class="api-call">
              <span class="endpoint">${call.method} ${call.endpoint}</span>
              <span class="status-badge ${call.status >= 200 && call.status < 300 ? 'success' : 'error'}">
                ${call.status}
              </span>
              <span style="color: #6b7280; margin-left: 8px;">${call.duration}ms</span>
            </div>
          `).join('') : ''}
        </div>
`;
      });
    }

    if (passedTests.length > 0) {
      html += '<h3 style="color: #22c55e; margin: 30px 0 15px;">✅ Passed Tests</h3>';
      
      passedTests.forEach(test => {
        html += `
        <div class="test-item success">
          <div class="test-header">
            <span class="test-name">${test.name}</span>
            <span class="test-status success">PASSED</span>
          </div>
          <div class="test-meta">
            <strong>Category:</strong> ${test.category} | 
            <strong>Auth:</strong> ${test.authMethod} | 
            <strong>Duration:</strong> ${test.duration}ms
            ${test.graphApiEndpoint ? ` | <strong>API:</strong> ${test.graphApiEndpoint}` : ''}
            ${test.httpMethod ? ` (${test.httpMethod})` : ''}
          </div>
          ${test.userInput ? `<div class="test-meta"><strong>User Input:</strong> "${test.userInput}"</div>` : ''}
          ${test.result ? `
            <div class="test-result">
              <strong>Result:</strong> ${JSON.stringify(test.result).substring(0, 200)}${JSON.stringify(test.result).length > 200 ? '...' : ''}
            </div>
          ` : ''}
          ${test.apiCalls && test.apiCalls.length > 0 ? test.apiCalls.map(call => `
            <div class="api-call">
              <span class="endpoint">${call.method} ${call.endpoint}</span>
              <span class="status-badge success">${call.status}</span>
              <span style="color: #6b7280; margin-left: 8px;">${call.duration}ms</span>
            </div>
          `).join('') : ''}
        </div>
`;
      });
    }

    html += `
      </div>
    </div>

    <div class="footer">
      <p><strong>Naible-Thread AI Email Agent</strong></p>
      <p>Generated at ${new Date().toLocaleString()}</p>
      <p style="margin-top: 10px; color: #9ca3af; font-size: 12px;">
        This report was automatically generated and sent via Microsoft Graph API
      </p>
    </div>
  </div>
</body>
</html>
`;

    return html;
  }

  /**
   * Generate plain text report for email clients that don't support HTML
   */
  private generatePlainTextReport(summary: TestSummary): string {
    const passRate = ((summary.passed / summary.totalTests) * 100).toFixed(1);
    
    let text = `
🧪 LIVE TEST RESULTS REPORT
═══════════════════════════════════════════════════════════

Execution ID: ${summary.executionId}
Executed: ${new Date(summary.timestamp).toLocaleString()}
User: ${summary.userEmail}

SUMMARY
───────────────────────────────────────────────────────────
Total Tests:  ${summary.totalTests}
Passed:       ${summary.passed} (${passRate}%)
Failed:       ${summary.failed}
Skipped:      ${summary.skipped}
Duration:     ${(summary.duration / 1000).toFixed(2)}s

AUTHENTICATION BREAKDOWN
───────────────────────────────────────────────────────────
`;

    Object.entries(summary.authBreakdown).forEach(([auth, stats]) => {
      if (stats.total > 0) {
        text += `${auth.toUpperCase().padEnd(20)} | Passed: ${stats.passed} | Failed: ${stats.failed} | Total: ${stats.total}\n`;
      }
    });

    text += `
GRAPH API CALL BREAKDOWN
───────────────────────────────────────────────────────────
`;

    Object.entries(summary.apiCallBreakdown).forEach(([endpoint, stats]) => {
      const shortEndpoint = endpoint.split('/').pop() || endpoint;
      text += `${shortEndpoint.padEnd(30)} | Passed: ${stats.passed} | Failed: ${stats.failed} | Total: ${stats.total}\n`;
    });

    text += `
CATEGORY BREAKDOWN
───────────────────────────────────────────────────────────
`;

    Object.entries(summary.categoryBreakdown).forEach(([category, stats]) => {
      text += `${category.padEnd(20)} | Passed: ${stats.passed} | Failed: ${stats.failed} | Total: ${stats.total}\n`;
    });

    // Failed tests
    const failedTests = summary.testResults.filter(t => t.status === 'failed');
    if (failedTests.length > 0) {
      text += `
❌ FAILED TESTS (${failedTests.length})
───────────────────────────────────────────────────────────
`;
      failedTests.forEach(test => {
        text += `
Test: ${test.name}
Category: ${test.category}
Auth: ${test.authMethod}
Duration: ${test.duration}ms
${test.graphApiEndpoint ? `API Endpoint: ${test.httpMethod} ${test.graphApiEndpoint}\n` : ''}${test.userInput ? `User Input: "${test.userInput}"\n` : ''}Error: ${test.error}
${test.errorDetails ? `Details: ${test.errorDetails}\n` : ''}
`;
      });
    }

    // Passed tests summary
    const passedTests = summary.testResults.filter(t => t.status === 'success');
    if (passedTests.length > 0) {
      text += `
✅ PASSED TESTS (${passedTests.length})
───────────────────────────────────────────────────────────
`;
      passedTests.forEach(test => {
        text += `✓ ${test.name} (${test.category}, ${test.duration}ms)\n`;
      });
    }

    text += `
═══════════════════════════════════════════════════════════
Generated at ${new Date().toLocaleString()}
Naible-Thread AI Email Agent - Automated Test Report
`;

    return text;
  }

  /**
   * Send test results via Outlook email
   */
  async sendViaOutlook(summary: TestSummary): Promise<{ success: boolean; messageId?: string; error?: string }> {
    if (!this.userId || !this.userEmail) {
      return { success: false, error: 'Reporter not initialized' };
    }

    try {
      console.log('📧 Generating and sending test report via Outlook...');

      const htmlReport = this.generateHTMLReport(summary);
      const plainTextReport = this.generatePlainTextReport(summary);

      const statusEmoji = summary.failed === 0 ? '✅' : '❌';
      const passRate = ((summary.passed / summary.totalTests) * 100).toFixed(1);

      // Get access token for Outlook
      const { getValidAccessToken } = await import('./oauthService');
      const accessToken = await getValidAccessToken('outlook', this.userId);

      if (!accessToken) {
        throw new Error('Outlook not connected. Cannot send report via email.');
      }

      // Send via Outlook API
      const response = await fetch('https://ben3ou87--outlook-send.functions.blink.new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessToken,
          to: [this.userEmail],
          subject: `${statusEmoji} Test Results: ${summary.passed}/${summary.totalTests} Passed (${passRate}%) - ${new Date().toLocaleDateString()}`,
          body: plainTextReport,
          htmlBody: htmlReport,
          importance: summary.failed > 0 ? 'high' : 'normal'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send email');
      }

      const result = await response.json();
      
      console.log('✅ Test report sent successfully via Outlook');
      console.log('📧 Message ID:', result.messageId);
      console.log(`📬 You should see the email in your Outlook inbox: ${this.userEmail}`);

      return { success: true, messageId: result.messageId };
    } catch (error) {
      console.error('Failed to send test report via Outlook:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Save test results to database for historical tracking
   */
  async saveToDatabase(summary: TestSummary): Promise<void> {
    if (!this.userId) {
      throw new Error('Reporter not initialized');
    }

    try {
      // Store summary in telemetry_reports table
      await blink.db.telemetryReports.create({
        id: summary.executionId,
        userId: this.userId,
        reportType: 'live_test_results',
        metrics: JSON.stringify({
          totalTests: summary.totalTests,
          passed: summary.passed,
          failed: summary.failed,
          skipped: summary.skipped,
          duration: summary.duration,
          authBreakdown: summary.authBreakdown,
          apiCallBreakdown: summary.apiCallBreakdown,
          categoryBreakdown: summary.categoryBreakdown
        }),
        context: JSON.stringify({
          testResults: summary.testResults.map(t => ({
            id: t.id,
            name: t.name,
            category: t.category,
            status: t.status,
            duration: t.duration,
            authMethod: t.authMethod,
            graphApiEndpoint: t.graphApiEndpoint,
            error: t.error
          }))
        }),
        timestamp: summary.timestamp
      });

      console.log('✅ Test results saved to database:', summary.executionId);
    } catch (error) {
      console.error('Failed to save test results to database:', error);
      throw error;
    }
  }

  /**
   * Get historical test results
   */
  async getHistoricalResults(limit: number = 10): Promise<TestSummary[]> {
    if (!this.userId) {
      throw new Error('Reporter not initialized');
    }

    const reports = await blink.db.telemetryReports.list({
      where: {
        userId: this.userId,
        reportType: 'live_test_results'
      },
      orderBy: { timestamp: 'desc' },
      limit
    });

    return reports.map((report: any) => {
      const metrics = safeJsonParse(report.metrics);
      const context = safeJsonParse(report.context);
      
      return {
        executionId: report.id,
        userId: report.userId,
        userEmail: this.userEmail!,
        timestamp: report.timestamp,
        ...metrics,
        testResults: context.testResults
      };
    });
  }
}

export const testResultReporter = new TestResultReporter();
